import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import styles from './ui-empty-state.css';
import { Attribute } from '../../decorators/attribute.decorator';
import { UIIconGlyphRedesign } from '../icon/ui-icon.types';
import { UIIcon } from '../icon/ui-icon';
import { element, trustedElement } from '../../global/ui-helpers';
import { getDebugMode } from '../../global/config';
import { HeadingTag } from '../../global/utils.type';
import { UIButtonbar } from '../buttonbar/ui-buttonbar';
import {
    UIEmptyStateButtonbarLayout,
    UIEmptyStateHeadingLevel,
    UIEmptyStateIllustrationType,
    UIEmptyStateSeverity,
    UIEmptyStateSize,
} from './ui-empty-state.type';
import { TabIndex } from '../../global/keyboard';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIEmptyState
 * @element ui-empty-state
 * @classdesc <code>ui-empty-state</code> shows up when there's nothing to display for the user. They can inform users about empty search results, features that haven't been set up, or even success or errors in some instances.
 * @property {UIEmptyStateSize} [size="large"] {@attr size} Text size.
 * @property {UIEmptyStateSeverity} [severity="info"] {@attr severity} Severity level. Used when <code>illustration-type="bubble"</code> to set the color and icon of the bubble.
 * @property {string} [heading] {@attr heading} Heading text.
 * @property {UIEmptyStateHeadingLevel} [headingLevel="2"] {@attr heading-level} Heading level from 2 till 6. You can use it to set the appropriate semantic level for the heading independent of the visual size set by <code>size</code> attribute. Note that for accessibility reasons, heading level 1 is not allowed - your page must have already supplied an <code>h1</code> element.
 * @property {UIEmptyStateIllustrationType} [illustrationType] {@attr illustration-type} Illustration type.
 *  {@desc graphic: Custom graphic illustration. Should be an SVG image provided with a slot. Can only be used with <code>size="medium"</code> and <code>size="large"</code>}
 *  {@desc bubble: A speech bubble shape with a preset severity icon defined with <code>severity</code> attribute. Can only be used with <code>size="medium"</code> and <code>size="large"</code>}
 *  {@desc glyph: An icon provided with <code>glyph</code> attribute. Can only be used with <code>size="small"</code>}
 * @property {UIIconGlyphRedesign} [glyph] {@attr glyph} The icon to be displayed when <code>illustrationType="glyph"</code>. Only relevant with <code>size="small"</code>.
 * @property {UIEmptyStateButtonbarLayout} [buttonbarLayout="horizontal"] {@attr buttonbar-layout} Button layout orientation in the <code>buttonbar</code> slot. Only applicable when the slot is used. Note: mobile displays always use vertical orientation regardless of this setting.
 * @slot {@name graphic} An optional slot for custom graphic illustration. Should be an SVG image (provided with an <code>&lt;svg&gt;</code> or an <code>&lt;img&gt;</code> tag).
 * @slot {@name buttonbar} An optional slot for action buttons. Only <code>ui-buttonbar</code> is allowed, with up to 2 <code>ui-button</code>s with <code>layout="secondary"</code> and optionally <code>layout="tertiary"</code>. <code>ui-buttonbar</code> attributes are setup automatically.
 * @slot
 * @example
 * <ui-empty-state heading="No data to display">
 *  <p>There is currently no data available. Please check back later.</p>
 *  <ui-buttonbar slot="buttonbar">
 *    <ui-button layout="secondary" label="Save"></ui-button>
 *    <ui-button layout="tertiary" label="Cancel"></ui-button>
 *  </ui-buttonbar>
 * </ui-empty-state>
 */

@Component({
    tag: 'ui-empty-state',
    styles,
})
export class UIEmptyState extends UIElement {
    @Attribute() size: UIEmptyStateSize = 'large';
    @Attribute() severity: UIEmptyStateSeverity = 'info';
    @Attribute() heading!: string;
    @Attribute({ type: Number }) headingLevel: UIEmptyStateHeadingLevel = 2;
    @Attribute()
    illustrationType!: UIEmptyStateIllustrationType;
    @Attribute() glyph!: UIIconGlyphRedesign;
    @Attribute() buttonbarLayout: UIEmptyStateButtonbarLayout = 'horizontal';

    /**
     * Default heading tag
     * This is also the fallback value used when incorrect heading level (such as h1) is specified.
     * @private
     */
    private static readonly DEFAULT_HEADING_TAG: HeadingTag = 'h2';

    /**
     * Speech bubble SVG icons for different severities.
     * @private
     */
    private static readonly BUBBLE_SVG_ICONS: Record<
        'info' | 'error' | 'success',
        string
    > = {
        info: `<svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M114.995 55.0981C112.766 -7.68691 15.1839 -7.71182 13.0056 55.0981C12.502 97.5857 45.831 96.1288 64.0127 120C82.1693 96.1163 115.486 97.5981 114.995 55.0981Z" fill="#EBF1FA"/>
<path d="M70 39C70 42.3137 67.3137 45 64 45C60.6863 45 58 42.3137 58 39C58 35.6863 60.6863 33 64 33C67.3137 33 70 35.6863 70 39Z" fill="#3865B5"/>
<path d="M58 55C58 51.6863 60.6863 49 64 49C67.3137 49 70 51.6863 70 55V75C70 78.3137 67.3137 81 64 81C60.6863 81 58 78.3137 58 75V55Z" fill="#3865B5"/>
</svg>
`,
        error: `<svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M114.995 55.0981C112.766 -7.68691 15.1839 -7.71182 13.0056 55.0981C12.502 97.5857 45.831 96.1288 64.0127 120C82.1693 96.1163 115.486 97.5981 114.995 55.0981Z" fill="#FFEEE7"/>
<path d="M58 44C58 40.6863 60.6863 38 64 38C67.3137 38 70 40.6863 70 44V46L68.398 62.0199C68.1721 64.2793 66.2708 66 64 66C61.7292 66 59.8279 64.2793 59.602 62.0199L58 46V44Z" fill="#C5131C"/>
<path d="M70 76C70 79.3137 67.3137 82 64 82C60.6863 82 58 79.3137 58 76C58 72.6863 60.6863 70 64 70C67.3137 70 70 72.6863 70 76Z" fill="#C5131C"/>
</svg>

`,
        success: `<svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M114.995 55.0981C112.766 -7.68691 15.1839 -7.71182 13.0056 55.0981C12.502 97.5857 45.831 96.1288 64.0127 120C82.1693 96.1163 115.486 97.5981 114.995 55.0981Z" fill="#ECF9F3"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M54.0589 71.5148L86.2118 39.1716C87.7646 37.6095 90.2826 37.6095 91.8354 39.1716C93.3882 40.7337 93.3882 43.2664 91.8354 44.8284L55.4648 81.4144C54.6883 82.1952 53.4294 82.1952 52.653 81.4144L36.1647 64.8284C34.6118 63.2664 34.6118 60.7336 36.1647 59.1716C37.7176 57.6096 40.2354 57.6096 41.7883 59.1716L54.0589 71.5148Z" fill="#257886"/>
</svg>
`,
    };

    /**
     * Renders the icon.
     * @private
     * @returns {UIIcon | null} The icon element, unless not available.
     */
    private renderIcon(): UIIcon | null {
        if (!this.glyph) {
            if (getDebugMode()) {
                console.warn(
                    'ui-empty-state: `glyph` attribute is required when `illustration-type` is set to `glyph`.'
                );
            }
            return null;
        }

        return element`<ui-icon class="ui-empty-state__icon" glyph="${this.glyph}"></ui-icon>`;
    }

    /**
     * Renders the bubble icon based on the severity.
     * @private
     * @returns {HTMLElement} The bubble icon element.
     */
    private renderBubbleIcon(): HTMLElement {
        const bubbleIcon: string = UIEmptyState.BUBBLE_SVG_ICONS[this.severity];

        return trustedElement`<div class="ui-empty-state__bubble-icon">${bubbleIcon}</div>` as HTMLElement;
    }

    /**
     * Logs warnings in case of incorrect attribute combinations (design-wise).
     * Note: the component will still render these.
     * @private
     */
    private validateIllustrationUsage(): void {
        if (!getDebugMode()) {
            return;
        }

        if (
            this.illustrationType === 'glyph' &&
            this.glyph &&
            this.size !== 'small'
        ) {
            console.warn(
                'ui-empty-state: `illustration-type="glyph"` can only be used with `size="small"`.',
                this
            );
        }

        if (
            (this.illustrationType === 'graphic' ||
                this.illustrationType === 'bubble') &&
            this.size === 'small'
        ) {
            console.warn(
                `ui-empty-state: \`illustration-type="${this.illustrationType}"\` cannot be used with \`size="small"\`. Use \`size="medium"\` or \`size="large"\`.`,
                this
            );
        }
    }

    /**
     * Logs warnings in case of incorrect buttonbar usage.
     * Note: the component will still render these.
     * @param {UIButtonbar} buttonbarEl - The slotted buttonbar element.
     * @private
     */
    private validateButtonbarUsage(buttonbarEl: UIButtonbar): void {
        if (!getDebugMode()) {
            return;
        }

        if (buttonbarEl.querySelector('ui-button[layout="primary"]')) {
            console.warn(
                'ui-empty-state: `ui-buttonbar` only allows `ui-button` elements with `layout="secondary"` or `layout="tertiary"`.',
                this
            );
        }
    }

    /**
     * Processes slotted <code>ui-buttonbar</code> element.
     * @param {Array<HTMLElement>} buttonbarEls - Elements assigned to the <code>buttonbar</code> slot.
     * @private
     * @returns {UIButtonbar | null}
     */
    private configureButtonbar(
        buttonbarEls: Array<HTMLElement>
    ): UIButtonbar | null {
        const buttonbarEl = buttonbarEls.find(
            (element): element is UIButtonbar =>
                element.tagName?.toLowerCase() === 'ui-buttonbar'
        );

        if (!buttonbarEl) {
            return null;
        }

        this.validateButtonbarUsage(buttonbarEl);

        buttonbarEl.setAttribute(
            'alignment',
            this.buttonbarLayout === 'vertical' ? 'vertical' : 'center'
        );
        buttonbarEl.setAttribute('layout', 'inline');
        buttonbarEl.classList.add('ui-empty-state__buttonbar');

        return buttonbarEl;
    }

    /**
     * Renders the illustration.
     * @private
     * @returns {UIIcon | HTMLElement | null} The illustration element, unless not available.
     */
    private renderIllustration(): UIIcon | HTMLElement | null {
        if (this.illustrationType === 'glyph') {
            return this.renderIcon();
        } else if (this.illustrationType === 'graphic') {
            const [graphicSlotEl] = this.getChildrenForSlot('graphic');
            return graphicSlotEl ?? null;
        }
        return null;
    }

    /**
     * Returns an appropriate heading tag based on the heading level attribute.
     * @private
     * @returns {HeadingTag} The heading tag.
     */
    private getHeadingTag(): HeadingTag {
        const rawHeadingLevel = this.getAttribute('heading-level')?.trim();
        const headingTagMatch = rawHeadingLevel?.match(/^h([2-6])$/i);

        if (headingTagMatch) {
            if (getDebugMode()) {
                console.warn(
                    `ui-empty-state: \`heading-level\` expects a number instead of a tag name.`,
                    this
                );
            }

            return `h${headingTagMatch[1]}` as HeadingTag;
        }

        const level = Number(this.headingLevel);
        if (Number.isInteger(level) && level >= 2 && level <= 6) {
            return `h${level}` as HeadingTag;
        }

        if (getDebugMode()) {
            console.warn(
                `ui-empty-state: Invalid heading level ${this.headingLevel}. Note h1 is not allowed. Falling back to default level ${UIEmptyState.DEFAULT_HEADING_TAG}.`,
                this
            );
        }

        return UIEmptyState.DEFAULT_HEADING_TAG;
    }

    static get observedAttributes(): Array<string> {
        return ['heading'];
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: string,
        newValue: string | null
    ): void {
        if (!this.hydrated) {
            return;
        }
        if (name === 'heading') {
            this.updateHeading(newValue);
        }
    }

    /**
     * Updates the painted heading when the host attribute changes.
     * @param {string | null} newValue
     * @private
     */
    private updateHeading(newValue: string | null): void {
        const headingText = newValue?.trim() ?? '';
        const headingEl = this.querySelector('.ui-empty-state__heading');

        if (headingEl) {
            if (headingText) {
                headingEl.textContent = headingText;
            } else {
                headingEl.remove();
            }
            return;
        }

        if (!headingText) {
            return;
        }

        const heading = this.createElement({
            tagName: this.getHeadingTag(),
            classList: {
                'ui-empty-state__heading': true,
            },
            attributes: {
                tabIndex: TabIndex.Inactive,
            },
            children: [headingText],
        });
        const content = this.querySelector('.ui-empty-state__content');
        content?.parentElement?.insertBefore(heading, content);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const headingTag: HeadingTag = this.getHeadingTag();
        const heading = this.heading?.trim();
        const buttonbarEls = this.getChildrenForSlot('buttonbar');
        const buttonbarEl = this.configureButtonbar(buttonbarEls);

        this.validateIllustrationUsage();

        this.updateElement({
            classList: {
                'ui-empty-state': true,
                [`ui-empty-state--size-${this.size}`]: true,
            },
        });
        const inner = this.createElement({
            tagName: 'section',
            classList: {
                'ui-empty-state__wrapper': true,
            },
            children: [
                {
                    tagName: 'div',
                    classList: {
                        'ui-empty-state__illustration': true,
                    },
                    children: [
                        this.renderIllustration(),
                        this.illustrationType === 'bubble'
                            ? this.renderBubbleIcon()
                            : null,
                    ],
                },
                heading
                    ? {
                          tagName: headingTag,
                          classList: {
                              'ui-empty-state__heading': true,
                          },
                          attributes: {
                              tabIndex: TabIndex.Inactive,
                          },
                          children: [heading],
                      }
                    : null,
                {
                    tagName: 'div',
                    classList: {
                        'ui-empty-state__content': true,
                    },
                    children: this.childNodes,
                },
                ...(buttonbarEl ? [buttonbarEl] : []),
            ],
        });

        this.append(inner);
    }
}
